import type {
  IGeneratedFileTypeOption,
  TGeneratedFilesArchiveErrorKey,
  TGeneratedFileSource,
  TGeneratedFilesSelection,
} from "../types/generated-files";

export const GENERATED_FILES_QUERY_KEY = "generated-files";

export const GENERATED_FILES_API_PATH = "/ai-generated-files";

// NOTE: NSAI-6501 — query keys for one file's share list and the projects it
// can be shared with; both are refetched after every share / remove.
export const GENERATED_FILE_SHARES_QUERY_KEY = "generated-file-shares";

// NOTE: NSAI-6501 — mirrors MAX_GENERATED_FILE_SHARE_TARGETS on the server: one
// share request takes at most this many people and this many projects, so the
// invite pickers stop adding at the limit instead of building a request the
// API would reject (and clearing the selection with it).
export const GENERATED_FILE_SHARE_MAX_TARGETS = 50 as const;

export const GENERATED_FILE_SHAREABLE_PROJECTS_QUERY_KEY =
  "generated-file-shareable-projects";

export const getGeneratedFileSharesPath = (fileUuid: string): string =>
  `${GENERATED_FILES_API_PATH}/${fileUuid}/shares`;

export const getGeneratedFileShareableProjectsPath = (
  fileUuid: string,
): string => `${GENERATED_FILES_API_PATH}/${fileUuid}/shareable-projects`;

// NOTE: Spreadsheets intentionally omitted — the app has no XLSX/CSV
// generator today (only PDF / DOCX / PPTX / PNG-JPEG image generation),
// so the filter would always show zero results. Add back when an
// XLSX-generator tool ships.
export const GENERATED_FILE_TYPE_OPTIONS: readonly IGeneratedFileTypeOption[] =
  [
    { value: "all", labelKey: "generated_files.filter_all" },
    { value: "image", labelKey: "generated_files.filter_images" },
    { value: "document", labelKey: "generated_files.filter_documents" },
    {
      value: "presentation",
      labelKey: "generated_files.filter_presentations",
    },
    // NOTE: NSAI-6327 — generated HTML pages (`text/html`), which no other
    // option matches.
    { value: "page", labelKey: "generated_files.filter_pages" },
  ] as const;

export const GENERATED_FILES_DEFAULT_LIMIT = 20 as const;

// NOTE: Mirrors apps/nsai/server/src/generated-files/generated-files.constants.ts
// MAX_LIMIT — the controller rejects anything above 100 via class-validator.
export const GENERATED_FILES_ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100] as const;

// NOTE: Both clients debounce the search box by the same amount so the
// request cadence — and the "is searching" affordance built on it — match.
export const GENERATED_FILES_SEARCH_DEBOUNCE_MS = 300 as const;

// NOTE: i18n keys (common namespace) for each source label. `as const` keeps
// the values as literal keys so the typed `t()` accepts them.
export const GENERATED_FILE_SOURCE_LABEL_KEYS = {
  chat: "generated_files.source_chat",
  agent: "generated_files.source_agent",
  webhook: "generated_files.source_webhook",
  eventRule: "generated_files.source_event_rule",
  automation: "generated_files.source_automation",
} as const satisfies Record<TGeneratedFileSource, string>;

// NOTE: NSAI-4326 — ceiling on a listed image a client will render inline from
// a base64 data URL, mirroring the server's `MAX_INLINE_MESSAGE_IMAGE_BYTES`
// for chat attachments. The reason is the same and it is not theoretical: base64
// costs ~1.33x the bytes on the wire and again in memory, and React Native hands
// the whole string to Fresco, which decodes it in one allocation — a 27 MB image
// killed the Android app with `OutOfMemoryError` against that runtime's 192 MB
// heap growth limit (NSAI-5158). Generated images can be far larger than a phone
// photo, so past this the file stays download-only.
export const GENERATED_FILE_IMAGE_PREVIEW_MAX_BYTES = 10 * 1024 * 1024;

// NOTE: NSAI-7393 — ceiling on a listed video the mobile client opens in its
// player. The clip does not stream: it is downloaded whole, read into a base64
// string and decoded into a byte array before it is written to the cache file
// the native player reads, so several copies of it sit in the JS heap at once.
// A generated clip is a few MB (8 s of Veo / Sora at 720p–1080p); past this the
// file stays download-only rather than risking the app on an outlier.
export const GENERATED_FILE_VIDEO_PREVIEW_MAX_BYTES = 50 * 1024 * 1024;

// NOTE: NSAI-5312 — downloading many generated files as one ZIP.
export const GENERATED_FILES_ARCHIVE_API_PATH = "/ai-generated-files/archive";

// NOTE: Mirror GENERATED_FILES_ARCHIVE_MAX_FILES / _MAX_TOTAL_BYTES in
// apps/nsai/server/src/generated-files/generated-files.constants.ts — keep both
// in sync. The server is the enforcement point; the clients use the count to
// stop an over-sized selection before the request and to word the limit.
export const GENERATED_FILES_ARCHIVE_MAX_FILES = 100;
export const GENERATED_FILES_ARCHIVE_MAX_TOTAL_BYTES = 500 * 1024 * 1024;

export const GENERATED_FILES_ARCHIVE_MIME_TYPE = "application/zip";

// NOTE: How many of a chat's files its "Download generated files" dialog
// lists: one listing page at the server's largest size (MAX_LIMIT), which is
// also the most one archive holds — so whatever the dialog shows can always be
// downloaded in one go. A chat with more says so and points to the page.
export const GENERATED_FILES_CHAT_DOWNLOAD_LIMIT =
  GENERATED_FILES_ARCHIVE_MAX_FILES;

// NOTE: Saved archives are named `<prefix>-<YYYY-MM-DD>.zip` on the user's own
// date — the same shape the server puts in Content-Disposition.
export const GENERATED_FILES_ARCHIVE_FILE_NAME_PREFIX = "ai-generated-files";

// NOTE: The fallback for any failure the status map does not name — including
// a failure with no status at all (a dropped connection mid-download).
export const GENERATED_FILES_ARCHIVE_DEFAULT_ERROR_KEY =
  "generated_files.archive.errors.download_failed" as const satisfies TGeneratedFilesArchiveErrorKey;

// NOTE: Mapped from the HTTP status, not the response body, because mobile's
// native downloader only ever reports the status (it streams to disk and never
// parses an error body) — so both clients word each refusal the same way.
export const GENERATED_FILES_ARCHIVE_ERROR_KEY_BY_STATUS: Readonly<
  Record<number, TGeneratedFilesArchiveErrorKey | undefined>
> = {
  401: "generated_files.archive.errors.access_denied",
  403: "generated_files.archive.errors.access_denied",
  404: "generated_files.archive.errors.unavailable",
  413: "generated_files.archive.errors.too_large",
  429: "generated_files.archive.errors.throttled",
};

// NOTE: NSAI-5312 — nothing ticked. Read-only, so one instance is shared: every
// selection change builds a new map (see toggleGeneratedFileSelection).
export const EMPTY_GENERATED_FILES_SELECTION: TGeneratedFilesSelection =
  new Map();

// NOTE: NSAI-5660 — the header that marks a chat-file read as a generated HTML
// page rendered in the preview, so the server audits it as `preview_html_page`
// instead of `download_chat_file`. Mirrors CHAT_FILE_READ_PURPOSE_HEADER on the
// server; allowed in its CORS `allowedHeaders`.
export const CHAT_FILE_READ_PURPOSE_HEADER = "x-file-read-purpose";
